import { engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { BetManager } from "../Managers/BetManager";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { BaseElementAttributes } from "../Components/Elements/Graphics/BaseElement";
import { ImageTextElementAttributes } from "../Components/Elements/Texts/ImageTextElement";

@engineComponent(ComponentTypes.DisplayObject)
export class ImageTextElementAdaptive extends ImageTextCounterElement {
    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    private maxWidth: number;

    private maxMultiplier: number;

    private currentScale: number;

    private originScale: number;

    private fixedDigitCount: boolean = false;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();

        this.maxWidth = -1;
        this.maxMultiplier = -1;
        this.currentScale = -1;
        this.originScale = 1;
    }

    //******************************************************************************************************************
    //  CREATE, UPDATE
    //******************************************************************************************************************

    public createByNode(node: Element) {
        super.createByNode(node);

        if (node.hasAttribute(ImageTextElementAdaptiveAttributes.MAX_WIDTH)) {
            this.maxWidth = parseInt(node.getAttribute(ImageTextElementAdaptiveAttributes.MAX_WIDTH));
        }

        if (node.hasAttribute(ImageTextElementAdaptiveAttributes.MAX_MULTIPLIER)) {
            this.maxMultiplier = parseInt(node.getAttribute(ImageTextElementAdaptiveAttributes.MAX_MULTIPLIER));
        }

        if (node.hasAttribute(BaseElementAttributes.SCALE)) {
            this.originScale = parseFloat(node.getAttribute("Scale"));
        }

        if (node.getAttribute("FixedDigitCount") == "true") {
            this.fixedDigitCount = true;
        }
    }

    public updateByNode(node: Element): void {
        super.updateByNode(node);

        if (node.hasAttribute(ImageTextElementAdaptiveAttributes.TINT)) {
            this.bitmapText.tint = parseInt(node.getAttribute(ImageTextElementAdaptiveAttributes.TINT), 16);
        }
    }

    //******************************************************************************************************************
    //  REALIGN
    //******************************************************************************************************************

    public realign(): void {
        //use always calculated scale based on max multiplier and max bet in case useMaxMultiplier is true
        if (this.fixedDigitCount) {
            let oldValue = this.bitmapText.text;
            this.bitmapText.scale.set(this.originScale);
            //this.bitmapText.text = NumberUtils.valueToNumber(8 * Math.pow(10, BetManager.DigitCount-1), (this.round || ImageTextCounterElement.Round) ? 0 : AppContext.gameConfiguration.ShowDecimalPlaces);
            this.bitmapText.scale.set(this.getAdaptiveScale());
            this.bitmapText.text = oldValue;
        } else if (this.maxMultiplier > -1 && this.scale.x != this.currentScale) {
            if (this.currentScale == -1) {
                this.currentScale = this.getScaleByMaxMultiplier();
            }
            this.bitmapText.scale.set(this.currentScale);
        } else if (this.maxWidth > -1) {
            //else check only max width attribute, if set, calculate adaptive scale for current value
            this.bitmapText.scale.set(this.originScale); //reset scale to get real width

            if (this.bitmapText.width > 0) {
                this.bitmapText.scale.set(this.getAdaptiveScale());
            }
        }

        switch (this.textAlign) {
            case ImageTextElementAttributes.TEXT_ALIGN_VALUE.RIGHT:
                this.bitmapText.position.x = -this.bitmapText.width;
                break;
            case ImageTextElementAttributes.TEXT_ALIGN_VALUE.CENTER:
                this.bitmapText.position.x = -this.bitmapText.width * 0.5;
                break;
            default:
                this.bitmapText.position.x = 0;
                break;
        }

        switch (this.textBaseline) {
            case ImageTextElementAttributes.TEXT_BASELINE_VALUE.BOTTOM:
                this.bitmapText.position.y = -this.bitmapText.height;
                break;
            case ImageTextElementAttributes.TEXT_BASELINE_VALUE.MIDDLE:
                this.bitmapText.position.y = -this.bitmapText.height * 0.5;
                break;
            default:
                this.bitmapText.position.y = 0;
                break;
        }
    }

    protected getScaleByMaxMultiplier(): number {
        if (BetManager.Coins == null) {
            return -1;
        }

        const originText = this.bitmapText.text;
        let scale = this.originScale;

        if (this.maxWidth != null) {
            this.bitmapText.text = originText;
            scale = this.getAdaptiveScale();
        }

        return scale;
    }

    protected getAdaptiveScale(): number {
        const scale = (this.maxWidth * this.fontScale) / this.bitmapText.width;
        return scale < this.originScale * this.fontScale
            ? scale
            : scale > this.originScale * this.fontScale
              ? this.originScale * this.fontScale
              : this.originScale * scale;
    }

    //******************************************************************************************************************
    //  CLONE
    //******************************************************************************************************************

    public clone(): ImageTextElementAdaptive {
        let element: ImageTextElementAdaptive = new ImageTextElementAdaptive();
        element.visible = this.visible;
        element.alpha = this.alpha;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.rotation = this.rotation;
        element.fontScale = this.fontScale;

        element.font = this.font;
        element.text = this.text;
        element.textAlign = this.textAlign;
        element.textBaseline = this.textBaseline;

        element.maxWidth = this.maxWidth;
        element.currentScale = this.currentScale;
        element.originScale = this.originScale;
        element.maxMultiplier = this.maxMultiplier;
        element.fixedDigitCount = this.fixedDigitCount;

        element.bitmapText = new PIXI.extras.BitmapText(this.text, { font: this.font });
        element.addChild(element.bitmapText);

        element.realign();
        element.markAsCloned();

        return element;
    }
}

export const ImageTextElementAdaptiveAttributes = {
    /**
     * Max possible multiplier in game used in values over symbols.
     * @property MaxWidth
     * @default -1
     */
    MAX_WIDTH: "MaxWidth",
    /**
     * Max possible multiplier in game used in values over symbols.
     * @property MaxMultiplier
     * @default -1
     */
    MAX_MULTIPLIER: "MaxMultiplier",
    TINT: "Tint",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/ImageTextElementAdaptive.ts
 **/